/**
 * useIndices — live NSE/BSE index data for the market ticker strip
 *
 * Polls Yahoo Finance every 60 seconds.
 * Falls back to stale data + shows an error state on failure.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchIndices, type IndexQuote } from '@/services/marketDataApi';

const POLL_INTERVAL_MS = 60_000; // 60 seconds (Yahoo free tier is 15-min delayed anyway)

interface UseIndicesReturn {
  indices:   IndexQuote[];
  loading:   boolean;
  error:     string | null;
  lastUpdated: Date | null;
  refresh:   () => void;
}

export function useIndices(): UseIndicesReturn {
  const [indices,     setIndices]     = useState<IndexQuote[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    try {
      const data = await fetchIndices();
      if (data.length > 0) {
        setIndices(data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        // Empty result — API reachable but returned nothing
        setError('No index data returned');
      }
    } catch (err) {
      setError('Could not load index data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return { indices, loading, error, lastUpdated, refresh: load };
}
